import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AlbumCard from '../components/AlbumCard'
import { Album } from '../types'
import { useAuthStore } from '../stores/authStore'
import { API_BASE_URL } from '../config'

const HomePage: React.FC = () => {
  const [albums, setAlbums] = useState<Album[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    fetchAlbums()
  }, [])

  const fetchAlbums = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`${API_BASE_URL}/api/albums`)
      
      if (!response.ok) {
        throw new Error('Failed to fetch albums')
      }
      
      const data = await response.json()
      setAlbums(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAlbum = async (id: number) => {
    if (!confirm('确定要删除这个相册吗？')) return

    try {
      const response = await fetch(`${API_BASE_URL}/api/albums/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.ok) {
        setAlbums(albums.filter(album => album.id !== id))
      }
    } catch (error) {
      console.error('Failed to delete album:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchAlbums}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          重试
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">相册集</h1>
        
        {isAuthenticated && (
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            管理相册
          </button>
        )}
      </div>

      {albums.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500 text-lg">暂无相册</p>
          {isAuthenticated && (
            <p className="text-gray-400 mt-2">前往管理页面创建第一个相册吧！</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {albums.map((album) => (
            <div
              key={album.id}
              onClick={() => navigate(`/album/${album.id}`)}
              className="cursor-pointer"
            >
              <AlbumCard
                album={album}
                onDelete={handleDeleteAlbum}
                isAdmin={isAuthenticated}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default HomePage
